import React, { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useSelector } from "react-redux";
import { getVesselEmissionOverview } from "../../../services/vesselEmissionService";
import { generateDateTicks } from "../../../services/dataTicks";
import { ChevronDown, SlidersHorizontal } from "lucide-react";

const emissionTypes = {
  co2: {
    name: "CO₂ Emissions",
    unit: "MT/day",
    color: "#1a5276",
  },
  ch4: {
    name: "CH₄ Emissions",
    unit: "MT/day",
    color: "#d35400",
  },
  n2o: {
    name: "N₂O Emissions",
    unit: "MT/day",
    color: "#27ae60",
  },
  co2equivalent: {
    name: "CO₂ Equivalent",
    unit: "MT/day",
    color: "#7d3c98",
  },
  ghg: {
    name: "GHG Intensity",
    // unit: "MT/day",
    unit: "g CO₂e/MJ",
    color: "#117864",
  },
  sox: {
    name: "SOₓ Emissions",
    unit: "MT/day",
    color: "#a93226",
  },
  nox: {
    name: "NOₓ Emissions",
    unit: "MT/day",
    color: "#b7950b",
  },
  pm: {
    name: "Particulate Matter",
    unit: "MT/day",
    color: "#566573",
  },
};

const EmissionOverviewScatterChart = ({ voyageId }) => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const [emissionData, setEmissionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([
    "co2", "ch4", "n2o", "co2equivalent", "ghg", "sox", "nox", "pm"
  ]);

  useEffect(() => {
    const fetchOverview = async () => {
      if (!currentVessel?._id) {
        console.log("No vessel selected, skipping fetch");
        return;
      }

      if (!voyageId && (!startDate || !endDate)) {
        console.log("No voyage selected and missing date range, skipping fetch");
        return;
      }

      setLoading(true);

      try {
        const response = await getVesselEmissionOverview(
          currentVessel._id,
          voyageId ? null : startDate,
          voyageId ? null : endDate,
          voyageId
        );

        if (response?.status === "error") {
          setError(response.message || "Failed to fetch emission data");
          setEmissionData([]);
        } else {
          setEmissionData(response.emissions || []);
          console.log("EmissionData1: ", setEmissionData)
          setError(null);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || "Unknown error occurred";
        setError(errorMessage);
        console.error("API Error:", err);
        setEmissionData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, [currentVessel, startDate, endDate, voyageId]);

  const handleToggle = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  // Generate scatter series data
  const generateSeries = () => {
    return selectedTypes.map(type => {
      const emissionType = emissionTypes[type];

      const data = emissionData
        .filter(item => item.emissions?.[type]?.current !== undefined)
        .map(item => ({
          x: new Date(item.date).getTime(),
          y: item.emissions[type].current,
          date: item.date,
          emissionType: emissionType.name,
          fullDate: new Date(item.date).toLocaleDateString("en-US", {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        }));

      return {
        name: emissionType.name,
        data: data,
        color: emissionType.color,
        marker: {
          symbol: 'circle'
        }
      };
    });
  };

  // Get unique dates for xAxis tick positions
  const dates = emissionData.map(item => item.date);

  let tickPositions = generateDateTicks(dates);
  // const uniqueDates = [...new Set(dates.map(d => new Date(d).getTime()))];

  const options = {
    chart: {
      type: "scatter",
      plotBorderWidth: 1,
      zooming: {
        type: "xy",
      },
      backgroundColor: "#ffffff",
      height: 500,
      style: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      },
    },
    title: {
      text: null,
    },
    xAxis: {
      type: "datetime",
      tickPixelInterval: 150,
      // tickPositions: uniqueDates,
      tickPositions: tickPositions,
      title: {
        text: "Date",
        style: {
          color: "#374151",
          fontWeight: "500",
        },
      },
      lineColor: "#e2e8f0",
      tickColor: "#e2e8f0",
      labels: {
        format: "{value:%b %d}",
        style: {
          color: "#6b7280",
        },
      },
      gridLineWidth: 1,
      gridLineColor: "#f3f4f6",
    },
    yAxis: {
      title: {
        text: "Emissions (MT/day)",
        style: {
          color: "#374151",
          fontWeight: "500",
        },
      },
      labels: {
        style: {
          color: "#6b7280",
        },
        formatter: function () {
          return this.value;
        }
      },
      gridLineWidth: 1,
      gridLineColor: "#f3f4f6",
      minPadding: 0,
      maxPadding: 0.05,
      tickAmount: 6,
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 6,
          states: {
            hover: {
              enabled: true,
              lineColor: "#fff",
              lineWidth: 2,
              radius: 8,
            },
          },
        },
        states: {
          hover: {
            marker: {
              enabled: true,
            },
          },
        },
      },
      series: {
        cursor: 'pointer',
        point: {
          events: {
            click: function () {
              // Handle point click if needed
            }
          }
        }
      }
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        // Get all series that have data at this x position (date)
        const points = this.series.chart.series.map(series => {
          const point = series.points.find(p => p.x === this.x);
          return point ? {
            color: series.color,
            name: series.name,
            y: point.y,
            emissionType: point.emissionType
          } : null;
        }).filter(point => point !== null);

        // Generate HTML for all points at this x position
        const pointsHTML = points.map(point => {
          const emissionTypeKey = Object.keys(emissionTypes).find(
            key => emissionTypes[key].name === point.emissionType
          );
          const unit = emissionTypes[emissionTypeKey]?.unit || "MT/day";

          return `
        <div style="display: flex; justify-content: space-between; align-items: center; padding: 2px 0;">
          <div style="display: flex; align-items: center; gap: 6px;">
            <div style="width: 8px; height: 8px; border-radius: 50%; background-color: ${point.color};"></div>
            <span style="font-size: 12px;">${point.name}:</span>
          </div>
          <span style="font-weight: 600; font-size: 12px;">${point.y} ${unit}</span>
        </div>
      `;
        }).join('');

        return `
      <div style="padding: 12px; min-width: 220px;">
        <div style="font-weight: 600; margin-bottom: 8px; color: #374151; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px;">
          ${new Date(this.x).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric"
        })}
        </div>
        <div style="max-height: 200px; overflow-y: auto;">
          ${pointsHTML}
        </div>
      </div>
    `;
      },
      backgroundColor: "rgba(255, 255, 255, 0.98)",
      borderColor: "#e2e8f0",
      borderRadius: 8,
      shadow: true,
      style: {
        fontSize: "12px"
      }
    },
    series: generateSeries(),
    credits: {
      enabled: false,
    },
    responsive: {
      rules: [{
        condition: {
          maxWidth: 600
        },
        chartOptions: {
          chart: {
            height: 400
          },
          legend: {
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'bottom'
          }
        }
      }]
    }
  };

  if (loading) {
    return <div className="p-4 text-center">Loading emission data...</div>;
  }

  if (error) {
    return <div className="p-4 text-center text-red-500">{error}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center rounded-tl-2xl rounded-tr-2xl">
        <h2 className="text-lg font-semibold text-gray-800">
          Emission Overview
        </h2>
      </div>

      {/* Emission Type Toggles */}
      <div className="flex items-center gap-4 px-6">
        <label className="text-base font-semibold text-gray-700 flex items-center gap-2">
          <SlidersHorizontal className="w-5 h-5 text-[#27ade3]" />
          Select by:
        </label>
        <div className="flex flex-wrap gap-2">
          {Object.keys(emissionTypes).map((type) => {
            const { name, color } = emissionTypes[type];
            const isActive = selectedTypes.includes(type);

            return (
              <button
                key={type}
                onClick={() => handleToggle(type)}
                className={`relative px-2 py-1.5 text-[12px] rounded-lg transition-all duration-200 flex items-center gap-2 overflow-hidden
                    ${isActive
                    ? "text-white shadow-md"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200"
                  }`}
                style={{
                  backgroundColor: isActive ? color : undefined,
                  borderColor: isActive ? color : "#e2e8f0",
                }}
              >
                {name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart */}
      <div className="p-2">
        {emissionData.length > 0 && generateSeries().some(series => series.data.length > 0) ? (
          <HighchartsReact highcharts={Highcharts} options={options} />
        ) : (
          <div className="text-center py-8 text-gray-500">
            No data available for the selected period
          </div>
        )}
      </div>
    </div>
  );
};

export default EmissionOverviewScatterChart;